import type { ListKind } from "shared";

/**
 * missing (list rows): a row that leaves this required field empty blocks saving with this message, instead of being
 * dropped as rows with an empty required field otherwise are.
 */
export type FieldDef = { key: string; label: string; type: "text" | "textarea" | "url" | "select";
  options?: { value: string; label: string }[]; required?: boolean; missing?: string;
  /** The input is disabled (and saved as empty) unless the row's `key` field equals `value`. */
  enabledWhen?: { key: string; value: string } };

const STATUS = [
  { value: "active", label: "Active" }, { value: "paused", label: "Paused" },
  { value: "discontinued", label: "Discontinued" }, { value: "pending", label: "Status unconfirmed" },
];
const LEVEL = [{ value: "federal", label: "Federal" }, { value: "state", label: "State" }, { value: "local", label: "Local" }];

export const PROGRAM_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", type: "text", required: true },
  { key: "slug", label: "Slug (URL name)", type: "text", required: true },
  { key: "categoryId", label: "Category", type: "select", required: true },
  { key: "departmentId", label: "Department", type: "select" },
  { key: "jurisdiction", label: "Level", type: "select", options: LEVEL, required: true },
  { key: "status", label: "Status", type: "select", options: STATUS, required: true },
  { key: "statusNote", label: "Status note", type: "text" },
  { key: "summary", label: "Summary (one or two sentences)", type: "textarea" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "whoFor", label: "Who it is for", type: "textarea" },
  { key: "benefits", label: "What the customer can get", type: "textarea" },
  { key: "eligibility", label: "Eligibility", type: "textarea" },
  { key: "incomeRules", label: "Income rules", type: "textarea" },
  { key: "limitations", label: "Limitations", type: "textarea" },
  { key: "officialUrl", label: "Official page", type: "url" },
  { key: "applyUrl", label: "Apply link", type: "url" },
  { key: "eligibilityCheckerUrl", label: "Eligibility checker", type: "url" },
  { key: "officeLocatorUrl", label: "Office locator", type: "url" },
  { key: "processingTime", label: "Processing time (only if an official source states it)", type: "text" },
  { key: "denialNextSteps", label: "If the application is denied", type: "textarea" },
];

export const STATE_FIELDS: FieldDef[] = [
  { key: "localName", label: "Name in this state", type: "text" },
  { key: "departmentId", label: "State agency", type: "select" },
  { key: "status", label: "Status in this state (blank = same as federal)", type: "select", options: STATUS },
  { key: "summary", label: "Summary", type: "textarea" },
  { key: "eligibility", label: "Eligibility", type: "textarea" },
  { key: "incomeRules", label: "Income rules", type: "textarea" },
  { key: "officialUrl", label: "Official page", type: "url" },
  { key: "applyUrl", label: "Apply link", type: "url" },
  { key: "eligibilityCheckerUrl", label: "Eligibility checker", type: "url" },
  { key: "officeLocatorUrl", label: "Office locator", type: "url" },
  { key: "processingTime", label: "Processing time", type: "text" },
  { key: "notes", label: "Notes", type: "textarea" },
];

export const DEPARTMENT_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", type: "text", required: true },
  { key: "slug", label: "Slug", type: "text", required: true },
  { key: "acronym", label: "Acronym", type: "text" },
  { key: "level", label: "Level", type: "select", options: LEVEL, required: true },
  { key: "stateCode", label: "State code (state/local only)", type: "text" },
  { key: "county", label: "County", type: "text" },
  { key: "parentId", label: "Part of", type: "select" },
  { key: "websiteUrl", label: "Website", type: "url" },
  { key: "description", label: "Description", type: "textarea" },
];

export const CATEGORY_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", type: "text", required: true },
  { key: "slug", label: "Slug", type: "text", required: true },
  { key: "parentId", label: "Parent category", type: "select" },
];

export const LIST_FIELDS: Record<ListKind, FieldDef[]> = {
  contacts: [
    { key: "kind", label: "Type", type: "select", required: true, options: ["phone", "tty", "text", "email", "address", "web"].map((v) => ({ value: v, label: v })) },
    { key: "label", label: "Label", type: "text", required: true },
    { key: "value", label: "Number / address", type: "text", required: true },
    { key: "hours", label: "Hours", type: "text" },
  ],
  documents: [{ key: "item", label: "Document", type: "text", required: true }, { key: "note", label: "Note", type: "text" }],
  steps: [{ key: "text", label: "Step", type: "textarea", required: true }],
  faqs: [{ key: "question", label: "Question", type: "text", required: true }, { key: "answer", label: "Answer", type: "textarea", required: true }],
  denial_reasons: [{ key: "reason", label: "Reason", type: "text", required: true }, { key: "whatToDo", label: "What to do", type: "textarea" }],
};

export const snakeToCamel = (o: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(o).map(([k, v]) => [k.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()), v]));

export const PROBLEM_FIELDS: FieldDef[] = [
  { key: "title", label: "Title (the caller's situation)", type: "text", required: true },
  { key: "slug", label: "Slug (URL name)", type: "text", required: true },
  { key: "categoryId", label: "Category", type: "select" },
  { key: "sort", label: "Sort order (lower comes first)", type: "text" },
  { key: "summary", label: "Summary", type: "textarea" },
  { key: "questionsToAsk", label: "Questions to ask (one per line)", type: "textarea" },
  { key: "infoNeeded", label: "Information to collect (one per line)", type: "textarea" },
  { key: "guidance", label: "Guidance for the rep", type: "textarea" },
  { key: "alternatives", label: "If no program fits", type: "textarea" },
  { key: "followUp", label: "Follow-up", type: "textarea" },
];

export const SYNONYM_FIELDS: FieldDef[] = [
  { key: "term", label: "When a rep types", type: "text", required: true },
  { key: "expansion", label: "Also search for (comma-separated)", type: "text", required: true },
];

export const INTENT_RULE_FIELDS: FieldDef[] = [
  { key: "pattern", label: "Pattern, e.g. {lost job | laid off} + {rent}", type: "text", required: true },
  { key: "problemId", label: "Problem", type: "select", required: true },
  { key: "weight", label: "Weight (0.1 to 5, default 1)", type: "text" },
];

/** A new row starts on "Choose a program", never on the first program of the list. */
export const problemProgramFields = (options: { value: string; label: string }[]): FieldDef[] => [
  { key: "programId", label: "Program", type: "select", required: true, missing: "choose a program",
    options: [{ value: "", label: "Choose a program" }, ...options] },
  { key: "reason", label: "Why it fits this problem (no eligibility rules, amounts or deadlines)", type: "text" },
];
